import { useMemo } from 'react';
import { EventStack, type BriefingEvent } from '@/features/briefing/BriefingCard';
import type { EventsCardData } from './types';

/**
 * Schedule card — the canonical "event stack + discussion" format: the agent's
 * short text reply carries the discussion, and this card is just the stacked
 * event rows (shared `EventStack` from the Today/briefing card) + an
 * Open-calendar link.
 *
 * The old format (week-density strip + day-grouped agenda with time rails) is
 * retired — product call 2026-06-09: one schedule visual language everywhere.
 * `hideStrip` is accepted for back-compat (the strip no longer exists).
 */

/** Parse an event timestamp to a LOCAL Date. A bare `YYYY-MM-DD` (all-day or
 *  date-only value) is constructed in local time rather than via `new Date()`,
 *  which would treat it as UTC midnight and shift it to the previous day in
 *  negative-UTC-offset timezones. */
function toLocalDate(s: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(s);
}

export function EventsResultCard({
  data,
  onOpenEvent,
  onOpenRange,
  hideOpenButton,
}: {
  data: EventsCardData;
  onOpenEvent: (e: { id: string; start: string }) => void;
  onOpenRange: () => void;
  /** Back-compat no-op — the retired week-density strip is gone entirely. */
  hideStrip?: boolean;
  hideOpenButton?: boolean;
}) {
  // Map + sort once per data change — this card lives inside the chat
  // container, which re-renders frequently while a reply streams.
  const events = useMemo<BriefingEvent[]>(
    () =>
      [...(data.events ?? [])]
        .sort((a, b) => toLocalDate(a.start).getTime() - toLocalDate(b.start).getTime())
        .map((ev) => ({
          // Click-through wants the real workspace object id (recurring
          // occurrences carry a `${masterId}:${occurrenceISO}` row id).
          id: ev.objectId ?? ev.id,
          title: ev.title,
          start: ev.start,
          end: ev.end ?? null,
          allDay: ev.allDay === true,
          attendees: (ev.attendees ?? []).map((a) => a.name || a.email).filter(Boolean),
        })),
    [data],
  );

  return (
    <div className="bc-card">
      <div className="bc-header">
        <span>
          Schedule · {data.rangeLabel} · {events.length} {events.length === 1 ? 'event' : 'events'}
        </span>
      </div>
      {events.length === 0 ? (
        <div className="bc-empty-day">Nothing scheduled — the window is wide open.</div>
      ) : (
        <EventStack events={events} onOpenEvent={(id, start) => onOpenEvent({ id, start: start ?? '' })} />
      )}
      {!hideOpenButton && (
        <button type="button" className="bc-link bc-link-cal" onClick={onOpenRange}>
          Open calendar <span aria-hidden>→</span>
        </button>
      )}
    </div>
  );
}
